import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Search, UserPlus } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Input } from '../../components/Input';
import { Avatar } from '../../components/Avatar';
import { Modal } from '../../components/Modal';
import { OptionCard } from '../../components/OptionCard';
import { PageHeader } from '../../components/PageHeader';
import { Pagination, usePagination } from '../../components/Pagination';
import { DateRangeFilter, inRange, ALL_TIME, type DateRange } from '../../components/DateRangeFilter';
import { Skeleton } from '../../components/Skeleton';
import { cn } from '../../lib/cn';
import { useAdminUsers, inviteAdminUser, type InviteResult, type CreateUserResult } from '../../api/admin';
import { InviteLinkResult } from '../../components/InviteLinkResult';
import { CreateOrInviteToggle, AccountCreatedPanel } from '../../components/CreateOrInvite';

// Must cover EVERY primaryRole the API can emit. platform_staff and reseller_staff were
// missing, and the API compounded it by reporting both as their *_admin equivalent — so
// a staff seat was displayed, and filtered, as a full admin.
const ROLE_LABEL: Record<string, string> = {
  platform_admin: 'Platform admin',
  platform_staff: 'Platform staff',
  reseller_admin: 'Reseller admin',
  reseller_staff: 'Reseller staff',
  business_admin: 'Business admin',
  business_staff: 'Business staff',
};

const ROLE_TONE: Record<string, 'info' | 'success' | 'warning' | 'neutral'> = {
  platform_admin: 'info',
  platform_staff: 'neutral',
  reseller_admin: 'success',
  reseller_staff: 'neutral',
  business_admin: 'warning',
  business_staff: 'neutral',
};

const STATUS_TONE = {
  active: 'success' as const,
  invited: 'info' as const,
  disabled: 'neutral' as const,
};

/** Cross-org users list for platform admin. */
export function Users() {
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const { data: users = [], isLoading } = useAdminUsers();

  // #11 — filters on the user's CREATED date.
  const [dateRange, setDateRange] = useState<DateRange>(ALL_TIME);

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (roleFilter !== 'all' && u.primaryRole !== roleFilter) return false;
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      if (!inRange(u.createdAt, dateRange)) return false;
      if (search && !u.name.toLowerCase().includes(search.toLowerCase()) && !u.email.includes(search.toLowerCase()) && !(u.username ?? '').toLowerCase().includes(search.toLowerCase()) && !u.primaryScope.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [users, roleFilter, statusFilter, search, dateRange]);

  // Locked decision: 25 rows by default, selector 25/50/100/200. Paginates the
  // FILTERED list, so filters and CSV export still see every matching row.
  const pager = usePagination(filtered);


  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Platform"
        title={<span>Users {isLoading ? <Skeleton className="inline-block w-12 h-5 rounded align-middle" /> : <span className="text-zinc-400 dark:text-zinc-500 font-medium tabular-nums">({users.length})</span>}</span>}
        subtitle="Every login across platform staff, resellers, and business owners."
        action={<Button iconLeft={<UserPlus className="w-4 h-4" />} onClick={() => setInviteOpen(true)}>Invite user</Button>}
      />

      <InviteUserModal open={inviteOpen} onClose={() => setInviteOpen(false)} />

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <FilterChip
            label="Role"
            value={roleFilter === 'all' ? 'All' : (ROLE_LABEL[roleFilter] ?? roleFilter)}
            // Derived from ROLE_LABEL so the filter can never drift from the roles the
            // API actually emits (platform_staff / reseller_staff were unfilterable).
            options={[{ v: 'all', l: 'All roles' }, ...Object.entries(ROLE_LABEL).map(([v, l]) => ({ v, l }))]}
            onChange={setRoleFilter}
          />
          <FilterChip
            label="Status"
            value={statusFilter === 'all' ? 'All' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
            options={[
              { v: 'all', l: 'All' },
              { v: 'active', l: 'Active' },
              { v: 'invited', l: 'Invited' },
              { v: 'disabled', l: 'Disabled' },
            ]}
            onChange={setStatusFilter}
          />
          <DateRangeFilter label="Created" value={dateRange} onChange={setDateRange} />
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search by name, username, email, or scope…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">User</th>
                <th className="text-left font-medium px-4 py-3">Role</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Scope</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Last sign-in</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
                        <div>
                          <Skeleton className="h-4 w-36 max-w-full rounded mb-1.5" />
                          <Skeleton className="h-3 w-44 max-w-full rounded" />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-24 rounded-full" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Skeleton className="h-4 w-28 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-4 py-3 hidden lg:table-cell"><Skeleton className="h-4 w-24 rounded" /></td>
                    <td className="px-5 sm:px-6 py-3"></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No users found.
                  </td>
                </tr>
              ) : (
                pager.pageItems.map((u) => (
                <tr key={u.id} className="group hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-5 sm:px-6 py-3">
                    <Link
                      to="/admin/users/$id"
                      params={{ id: u.id }}
                      className="flex items-center gap-3 min-w-0"
                    >
                      <Avatar name={u.name} size="md" />
                      <div className="min-w-0">
                        <div className="font-medium text-zinc-950 dark:text-zinc-50 truncate group-hover:text-brand dark:group-hover:text-brand-dm group-hover:underline transition flex items-center gap-1">
                          {u.name}
                          <ChevronRight className="w-3.5 h-3.5 text-zinc-400 opacity-0 group-hover:opacity-100 transition" />
                        </div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                          {u.username && <span className="font-mono text-zinc-600 dark:text-zinc-300">@{u.username}</span>}
                          {u.username && u.email && u.email !== u.username && <span className="mx-1 text-zinc-300 dark:text-zinc-600">·</span>}
                          {u.email !== u.username && u.email}
                        </div>
                      </div>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={ROLE_TONE[u.primaryRole]}>{ROLE_LABEL[u.primaryRole]}</Badge>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 hidden md:table-cell">{u.primaryScope}</td>
                  <td className="px-4 py-3">
                    <Badge tone={STATUS_TONE[u.status]}>
                      {u.status.charAt(0).toUpperCase() + u.status.slice(1)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums hidden lg:table-cell">{u.lastLogin ?? '—'}</td>
                  <td className="px-5 sm:px-6 py-3 text-right">
                    <ChevronRight className="w-4 h-4 text-zinc-400 inline-block" aria-hidden="true" />
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination state={pager} label="users" />

        <div className="px-5 sm:px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 text-xs text-zinc-500 dark:text-zinc-400">
          Matching <span className="tabular-nums">{filtered.length}</span> of <span className="tabular-nums">{users.length}</span>
        </div>
      </Card>
    </div>
  );
}

/** Invite a platform teammate (admin Users). On success shows the copyable accept link. */
function InviteUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [action, setAction] = useState<'create' | 'invite'>('invite');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'platform_admin' | 'platform_staff'>('platform_staff');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InviteResult | CreateUserResult | null>(null);

  function reset() {
    setAction('invite');
    setEmail('');
    setName('');
    setRole('platform_staff');
    setPassword('');
    setError(null);
    setResult(null);
    setSubmitting(false);
  }
  function close() {
    reset();
    onClose();
  }

  async function send() {
    if (action === 'create' && password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await inviteAdminUser({
        action,
        email: email.trim(),
        name: name.trim() || undefined,
        role,
        password: action === 'create' ? password : undefined,
      });
      setResult(res);
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    } catch {
      setError(
        action === 'create'
          ? 'Could not create the account. Check the details and try again.'
          : 'Could not send the invitation. Check the email and try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={result ? ('inviteUrl' in result ? 'Invitation sent' : 'Account created') : 'Add a platform teammate'}
      size="md"
      footer={
        result ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button onClick={send} disabled={!email.trim() || submitting} loading={submitting}>
              {action === 'create' ? 'Create account' : 'Send invite'}
            </Button>
          </>
        )
      }
    >
      {result ? (
        'inviteUrl' in result ? (
          <InviteLinkResult email={result.email} inviteUrl={result.inviteUrl} />
        ) : (
          <AccountCreatedPanel email={result.email} />
        )
      ) : (
        <div className="space-y-4">
          <CreateOrInviteToggle action={action} onChange={setAction} />
          <Input label="Email" required type="email" placeholder="teammate@itcitysoft.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input label="Full name" placeholder="(optional)" value={name} onChange={(e) => setName(e.target.value)} />
          {action === 'create' && (
            <Input label="Password" required type="password" hint="Minimum 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} />
          )}
          <div>
            <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
              Role <span className="text-rose-600">*</span>
            </div>
            <div className="space-y-2">
              <OptionCard selected={role === 'platform_admin'} onSelect={() => setRole('platform_admin')} asLabel={false}>
                <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">Platform admin</div>
                <div className="text-xs text-zinc-600 dark:text-zinc-300">Full access to every platform setting, reseller, and business.</div>
              </OptionCard>
              <OptionCard selected={role === 'platform_staff'} onSelect={() => setRole('platform_staff')} asLabel={false}>
                <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">Platform staff</div>
                <div className="text-xs text-zinc-600 dark:text-zinc-300">Day-to-day operations; can't change platform-wide configuration.</div>
              </OptionCard>
            </div>
          </div>
          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
        </div>
      )}
    </Modal>
  );
}

function FilterChip({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ v: string; l: string }>;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
      >
        <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
        <span>{value}</span>
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 z-20 min-w-[180px] rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 shadow-md py-1">
            {options.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => {
                  onChange(o.v);
                  setOpen(false);
                }}
                className={cn(
                  'block w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800',
                  'text-zinc-700 dark:text-zinc-200',
                )}
              >
                {o.l}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
